/**
 * TrialProgressBar — top-of-dashboard widget that creates urgency and
 * shows tangible progress through the 14-day / 20-actions-per-agent
 * trial. Three psychological mechanics built in:
 *
 *   1. **Day progress** — visual countdown ("Day 4 / 14") creates
 *      Zeigarnik effect: open loop the brain wants to close.
 *   2. **Agent breadth** — "3 / 11 agents tried" reframes the trial
 *      as a sample buffet rather than a clock.
 *   3. **Loss aversion** — "€198 / mes ušetríš pri Bundle 3-za-2"
 *      anchors the value left on the table if they don't upgrade.
 *
 * Hides itself when the workspace is on a paid plan (status.paid=true).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { Clock, Sparkles, TrendingUp, ChevronRight } from "lucide-react";

const SAVINGS_PER_AGENT = 79;  // blended average; Bundle 3-za-2 anchor

export default function TrialProgressBar({ workspaceId, accent = "#FFB800" }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/trial-quota/${workspaceId}`);
        if (!cancelled) setStatus(r.data);
      } catch (e) {
        // 404 (no workspace) — fail silent, bar stays hidden
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (!status || status.paid) return null;

  const trialDays = status.trial_days || 14;
  const daysLeft = Math.max(0, status.days_left || 0);
  const daysUsed = Math.max(0, trialDays - daysLeft);
  const dayPct = Math.min(100, Math.round((daysUsed / trialDays) * 100));

  const agentsTried = (status.agents || []).filter((a) => (a.used || 0) > 0).length;
  const totalAgents = 11;
  const breadthPct = Math.min(100, Math.round((agentsTried / totalAgents) * 100));

  // Savings anchor: 3-za-2 bundle = €198/mo; if user picks Bundle, they
  // unlock 3 agents simultaneously → that's the comparison anchor.
  const monthlySavingsAnchor = Math.max(198, agentsTried * SAVINGS_PER_AGENT);

  const urgent = daysLeft <= 3;
  const exhaustedAgents = (status.agents || []).filter((a) => a.used >= a.limit).length;

  return (
    <section
      data-testid="trial-progress-bar"
      className={`mb-6 border ${urgent ? "border-amber-500/60 bg-amber-950/20" : "border-gray-800 bg-neutral-950"} p-4 sm:p-5`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-black mb-1" style={{ color: accent }}>
            <Sparkles className="w-3 h-3 inline mr-1.5" />
            14-day Free Trial
          </p>
          <p className="text-sm text-gray-200">
            <span className="font-black text-white tabular-nums" data-testid="trial-days-used">
              Day {daysUsed} / {trialDays}
            </span>
            <span className="text-gray-500 mx-2">·</span>
            <span data-testid="trial-agents-tried">
              {agentsTried} / {totalAgents} agentov vyskúšaných
            </span>
            {exhaustedAgents > 0 && (
              <>
                <span className="text-gray-500 mx-2">·</span>
                <span className="text-amber-300 font-bold" data-testid="trial-exhausted-count">
                  {exhaustedAgents} agentov vyčerpaných
                </span>
              </>
            )}
          </p>
        </div>
        <a
          href="/agent-os/eshop#pricing"
          data-testid="trial-upgrade-cta"
          className="cta-trial-upgrade inline-flex items-center gap-1.5 px-4 py-2 text-black font-black uppercase tracking-widest text-[11px] shrink-0"
          style={{ backgroundColor: accent }}
        >
          Upgrade Plán
          <ChevronRight className="w-3.5 h-3.5" />
        </a>
      </div>

      {/* Two-track progress visualisation */}
      <div className="space-y-2.5">
        {/* Day track */}
        <ProgressRow
          icon={<Clock className="w-3.5 h-3.5" style={{ color: accent }} />}
          label={
            urgent
              ? `Iba ${daysLeft} dní zostáva`
              : `${daysLeft} dní zostáva`
          }
          pct={dayPct}
          color={urgent ? "#F59E0B" : accent}
          testid="trial-day-progress"
        />
        {/* Agent breadth track */}
        <ProgressRow
          icon={<Sparkles className="w-3.5 h-3.5 text-emerald-400" />}
          label={`Vyskúšané: ${agentsTried} z ${totalAgents}`}
          pct={breadthPct}
          color="#10B981"
          testid="trial-breadth-progress"
        />
      </div>

      {/* Loss-aversion footer */}
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-900 text-xs text-gray-400">
        <TrendingUp className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <span>
          Pri Bundle 3-za-2 ušetríš <span className="text-emerald-300 font-black tabular-nums" data-testid="trial-savings-anchor">€{monthlySavingsAnchor}</span> mesačne oproti single-agent cenám.
        </span>
      </div>
    </section>
  );
}

function ProgressRow({ icon, label, pct, color, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="shrink-0 w-5">{icon}</div>
      <div className="flex-1 h-1.5 bg-neutral-900 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <div className="text-[11px] text-gray-400 font-bold w-32 text-right shrink-0">{label}</div>
    </div>
  );
}
